import React from 'react';
import { Box, Card, CardContent, Typography, Chip, keyframes } from '@mui/material';
import {
  Psychology as PsychologyIcon,
  LocalGasStation as StationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0px rgba(139,92,246,0); }
  50% { box-shadow: 0 0 18px rgba(139,92,246,0.22), 0 0 32px rgba(99,102,241,0.12); }
`;

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

export interface InferenceItem {
  pollItemId: string;
  name: string;
  type: 'station' | 'attendant';
  inferredCount: number;
  selectedCount: number;
  gap: number;
}

export interface InferenceData {
  id: string;
  enabled: boolean;
  lastRunAt: string | null;
  inferences: InferenceItem[];
}

interface Props {
  data: InferenceData | null;
  loading: boolean;
}

const SkeletonCard: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 280,
    }}
  />
);

const formatLastRun = (iso: string | null): string => {
  if (!iso) return 'ainda não rodou';
  try {
    const date = new Date(iso);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'data inválida';
  }
};

const InferenceCard: React.FC<Props> = ({ data, loading }) => {
  if (loading) return <SkeletonCard />;

  // Card inteiro some se não está habilitado ou não tem inferências
  if (!data || !data.enabled || data.inferences.length === 0) return null;

  const topItems = data.inferences.slice(0, 10);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE_OUT }}
    >
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid rgba(139,92,246,0.18)',
          animation: `${pulseGlow} 4s ease-in-out infinite`,
          background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(250,245,255,0.6) 100%)',
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: 2,
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 12px rgba(139,92,246,0.35)',
                  flexShrink: 0,
                }}
              >
                <PsychologyIcon sx={{ color: '#fff', fontSize: 17 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
                Tags da IA — o que os clientes escreveram
              </Typography>
            </Box>
            <Chip
              label={`Última análise: ${formatLastRun(data.lastRunAt)}`}
              size="small"
              sx={{
                bgcolor: '#f5f3ff',
                color: '#7c3aed',
                fontSize: '0.68rem',
                fontWeight: 600,
                height: 22,
                border: '1px solid rgba(139,92,246,0.2)',
                flexShrink: 0,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 2, ml: 5.75 }}>
            A IA leu os comentários e identificou estes tópicos. O "gap" mostra quantos clientes escreveram algo relacionado mas não marcaram o pollItem no app.
          </Typography>

          {/* Items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {topItems.map((item, idx) => (
              <motion.div
                key={item.pollItemId}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.04, duration: 0.25, ease: EASE_OUT }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 1.5,
                    border: '1px solid rgba(139,92,246,0.15)',
                    bgcolor: 'rgba(139,92,246,0.04)',
                    transition: 'background 0.15s, border-color 0.15s',
                    '&:hover': {
                      bgcolor: 'rgba(139,92,246,0.08)',
                      borderColor: 'rgba(139,92,246,0.3)',
                    },
                  }}
                >
                  {/* Rank */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: '#8b5cf6',
                      width: 18,
                      textAlign: 'right',
                      flexShrink: 0,
                      fontSize: '0.78rem',
                    }}
                  >
                    {idx + 1}
                  </Typography>

                  {/* Type icon */}
                  <Box sx={{ flexShrink: 0, color: '#94a3b8' }}>
                    {item.type === 'station'
                      ? <StationIcon sx={{ fontSize: 14 }} />
                      : <PersonIcon sx={{ fontSize: 14 }} />
                    }
                  </Box>

                  {/* Name */}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 600,
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      minWidth: 0,
                    }}
                    noWrap
                  >
                    {item.name}
                  </Typography>

                  {/* Counts */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#7c3aed',
                        fontWeight: 800,
                        fontSize: '0.78rem',
                      }}
                    >
                      {item.inferredCount}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', fontSize: '0.7rem' }}
                    >
                      inferidos
                    </Typography>

                    {item.gap > 0 && (
                      <Chip
                        label={`+${item.gap} gap`}
                        size="small"
                        sx={{
                          bgcolor: '#fef3c7',
                          color: '#b45309',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          height: 18,
                          border: '1px solid rgba(245,158,11,0.3)',
                          '& .MuiChip-label': { px: 0.6 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default InferenceCard;
